import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useReconciliationBadge } from '../lib/ReconciliationBadgeContext';
import { LayoutDashboard, Package, ChefHat, ArrowLeftRight, ClipboardCheck, BrainCircuit, LogOut, BookOpen, ShoppingCart, Upload, AlertTriangle, CookingPot } from 'lucide-react';
import { cn } from '../lib/utils';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { pendingCount } = useReconciliationBadge();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/ingredients', icon: Package, label: 'Ingredients' },
    { to: '/recipes', icon: BookOpen, label: 'Recipes' },
    { to: '/prep', icon: ChefHat, label: 'Prep' },
    { to: '/prep-planning', icon: CookingPot, label: 'Prep Planning' },
    { to: '/transfers', icon: ArrowLeftRight, label: 'Transfers' },
    { to: '/procurement', icon: ShoppingCart, label: 'Procurement' },
    { to: '/sales-upload', icon: Upload, label: 'Sales Upload' },
    { to: '/audits', icon: ClipboardCheck, label: 'Audits' },
    { to: '/reconciliation', icon: AlertTriangle, label: 'Reconciliation' },
  ];

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="main-layout">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-black tracking-tight" data-testid="app-title">1OS Inventory</h1>
                <p className="text-xs text-zinc-400">Outlet: {user?.outlet_id || 'Main'}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#27272a] hover:bg-[#3f3f46] transition-colors text-sm font-medium"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-[#27272a] bg-[#09090b]" data-testid="main-navigation">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex gap-1 overflow-x-auto">
            {navItems.map((item) => {
              const isReconciliation = item.to === '/reconciliation';
              const showBadge = isReconciliation && pendingCount > 0;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                      isActive
                        ? 'border-emerald-500 text-emerald-500'
                        : 'border-transparent text-zinc-400 hover:text-white'
                    )
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-4 h-4" />
                  {showBadge ? (
                    <span>
                      {item.label}
                      <span
                        className="ml-2 text-zinc-500"
                        data-testid="nav-reconciliation-badge"
                      >
                        · {pendingCount}
                      </span>
                    </span>
                  ) : (
                    item.label
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-8 py-6">
        {children}
      </main>
    </div>
  );
};

export default Layout;
